/**
 * Settings Page with Navigation
 * Uses shadcn/ui components for consistent styling
 */

import { Link, useLocation, Routes, Route, Navigate } from 'react-router';
import {
    Settings,
    Database,
    Users,
    ShieldCheck,
    Bookmark,
    Webhook,
    Workflow,
    GitBranch,
    LinkIcon,
    Unlink,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

// Settings sub-pages
import { RolesPage, RoleDetailPage } from './roles';
import { PoliciesPage, PolicyDetailPage } from './policies';
import { CollectionsPage, CollectionDetailPage } from './data-model';
import { PresetListPage, PresetDetailPage } from './presets';
import { DBDiagramPage } from './db-diagram';
import { LinkCheckPage } from './link-check';
import { DeadLinksPage } from './dead-links';

const settingsNav = [
    { label: 'Project', path: '/admin/settings', icon: Settings, exact: true },
    { label: 'Data Model', path: '/admin/settings/data-model', icon: Database },
    { label: 'DB Diagram', path: '/admin/settings/db-diagram', icon: GitBranch },
    { label: 'User Roles', path: '/admin/settings/roles', icon: Users },
    { label: 'Access Policies', path: '/admin/settings/policies', icon: ShieldCheck },
    { label: 'Presets', path: '/admin/settings/presets', icon: Bookmark },
    { label: 'Link Check', path: '/admin/settings/link-check', icon: LinkIcon },
    { label: 'Dead Links', path: '/admin/settings/dead-links', icon: Unlink },
    { label: 'Webhooks', path: '/admin/settings/webhooks', icon: Webhook },
    { label: 'Flows', path: '/admin/settings/flows', icon: Workflow },
];

function SettingsHome() {
    return (
        <Card>
            <CardContent>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {settingsNav.slice(1).map((item) => {
                        const Icon = item.icon;
                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                className="flex items-center gap-3 rounded-lg border bg-background p-4 transition-colors hover:bg-accent"
                            >
                                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                                    <Icon className="h-5 w-5" />
                                </div>
                                <div className="font-medium">
                                    {item.label}
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </CardContent>
        </Card>
    );
}

function PlaceholderPage({ title }: { title: string }) {
    return (
        <Card>
            <CardHeader>
                <CardTitle>{title}</CardTitle>
            </CardHeader>
            <CardContent>
                <p className="text-muted-foreground">Coming soon.</p>
            </CardContent>
        </Card>
    );
}

export function SettingsPage() {
    const location = useLocation();

    const isActive = (path: string, exact?: boolean) => {
        if (exact) {
            return location.pathname === path;
        }
        return location.pathname.startsWith(path);
    };

    return (
        <div className="space-y-6">
            <div className="flex gap-6">
                {/* Settings Navigation */}
                <nav className="w-48 shrink-0 space-y-1">
                    {settingsNav.map((item) => {
                        const Icon = item.icon;
                        const active = isActive(item.path, item.exact);

                        return (
                            <Button
                                key={item.path}
                                variant={active ? 'default' : 'ghost'}
                                className={cn(
                                    'w-full justify-start gap-2',
                                    !active && 'text-muted-foreground'
                                )}
                                asChild
                            >
                                <Link to={item.path}>
                                    <Icon className="h-4 w-4" />
                                    {item.label}
                                </Link>
                            </Button>
                        );
                    })}
                </nav>

                {/* Settings Content */}
                <div className="min-w-0 flex-1">
                    <Routes>
                        <Route index element={<SettingsHome />} />
                        <Route path="data-model" element={<CollectionsPage />} />
                        <Route path="data-model/:collection" element={<CollectionDetailPage />} />
                        <Route path="db-diagram" element={<DBDiagramPage />} />
                        <Route path="roles" element={<RolesPage />} />
                        <Route path="roles/:id" element={<RoleDetailPage />} />
                        <Route path="policies" element={<PoliciesPage />} />
                        <Route path="policies/:id" element={<PolicyDetailPage />} />
                        <Route path="presets" element={<PresetListPage />} />
                        <Route path="presets/+" element={<PresetDetailPage />} />
                        <Route path="presets/:id" element={<PresetDetailPage />} />
                        <Route path="link-check" element={<LinkCheckPage />} />
                        <Route path="dead-links" element={<DeadLinksPage />} />
                        <Route path="webhooks" element={<PlaceholderPage title="Webhooks" />} />
                        <Route path="flows" element={<PlaceholderPage title="Flows" />} />
                        <Route path="*" element={<Navigate to="/admin/settings" replace />} />
                    </Routes>
                </div>
            </div>
        </div>
    );
}
